import React, {
  useState,
  ChangeEvent,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import {Box, Typography, FormControlLabel, FormHelperText} from '@mui/material';
import {t} from 'i18next';
import {AurTextField} from '../../../../common/aur-text-field/AurTextField';
import AurButton from '../../../../common/aur-button/AurButton';
import AurCheckbox from '../../../../common/aur-checkbox/AurCheckbox';
import {CreateProjectFormDataType} from '../types/createProjectTypes';
import {ReactComponent as FailedIcon} from '../../../../../assests/svg/info-outlined-red.svg';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import AurCountryFlag from '../../../../common/aur-country-flag/AurCountryFlag';
import {selectUser} from '../../../../../redux/slices/userSlice';
import {getUTCDateTimeForDisplay} from '../../../../../utils/utils';
import {
  FULL_UTC_DATE_FORMAT,
  IS_HOT_LEAD_FORM_VISIBLE,
} from '../../../../../constants/constants';
import {createProject} from '../../../../../api/SelectiveSelfManagementService';
import {showErrorToast} from '../../../../../api/utils';
import {useToast} from '../../../../../contexts/toastContext/ToastContext';
import AurLoadingIndicator from '../../../../common/aur-loading-indicator/AurLoadingIndictor';

const ACKNOWLEDGEMENT_PHRASE = 'i understand';
const STRING_PATH = 'projectCreation.creation.review.';

interface ReviewAndAcknoledgementProps {
  onSaveNContinue: () => void;
  projectInfo: CreateProjectFormDataType;
  onEditDetails: () => void;
  hotLeadBanner: React.ReactNode;
  genAIEnabled: boolean;
  userId: string;
  clientId: string;
}

const KeyValueRow = React.memo(
  ({
    label,
    value,
    geography,
  }: {
    label: string;
    value?: string;
    geography?: string;
  }) => (
    <Box className="key-value-main-div">
      <Typography className="typo-text label2_size key-text">
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={1}>
        {geography && (
          <AurCountryFlag countryCode={geography} altText={geography} />
        )}
        <Typography className="typo-text body2_size value-text text-wrap">
          {value || '--'}
        </Typography>
      </Box>
    </Box>
  )
);

const ReviewAndAcknoledgement = ({
  onSaveNContinue,
  projectInfo,
  onEditDetails,
  hotLeadBanner,
  genAIEnabled,
  userId,
  clientId,
}: ReviewAndAcknoledgementProps) => {
  const [confirmed, setConfirmed] = useState(false);
  const [ackText, setAckText] = useState('');
  const [isError, setIsError] = useState(false);
  const [ackTimestamp, setAckTimestamp] = useState<string | null>(null);
  const [ackTime, setAckTime] = useState<Date | null>();
  const [ackErrorMsg, setAckErrorMsg] = useState<string | null>(null);
  const user = useSelector(selectUser);
  const {serviceAgreementList} = useSelector(
    (state: RootState) => state.projectCreation
  );
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {showToast} = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const selectedAgreement = useMemo(
    () =>
      serviceAgreementList.find(
        (opt) => opt.id === projectInfo.serviceAgreement
      ),
    [serviceAgreementList, projectInfo.serviceAgreement]
  );

  const handleConfirm = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setConfirmed(checked);
    if (!checked) {
      setAckText('');
      setIsError(false);
      setAckErrorMsg(null);
      setAckTimestamp(null);
    }
  }, []);

  const canSubmit =
    confirmed && ackText.trim().toLowerCase() === ACKNOWLEDGEMENT_PHRASE;

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const text = e.target.value.trim().toLowerCase();
      if (text === ACKNOWLEDGEMENT_PHRASE) {
        setIsError(false);
        setAckErrorMsg(null);
        setAckTimestamp(
          t(STRING_PATH + 'signedBy', {
            name: user?.name,
            date: getUTCDateTimeForDisplay(new Date(), FULL_UTC_DATE_FORMAT),
          })
        );
      } else {
        setIsError(true);
        setAckTimestamp(null);
        setAckErrorMsg(t(STRING_PATH + 'helperText'));
      }
    },
    [user]
  );

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const timeStamp = new Date();
      setAckText(e.target.value);
      setAckTimestamp(
        t(STRING_PATH + 'signedBy', {
          name: user?.name,
          date: getUTCDateTimeForDisplay(timeStamp, FULL_UTC_DATE_FORMAT),
        })
      );
      setAckTime(timeStamp);
      setIsError(false);
      setAckErrorMsg(null);
    },
    [user]
  );

  const onReviewSubmit = async () => {
    setIsLoading(true);
    const {
      projectName,
      projectDescription,
      clientId: clientPreferredId,
      overseeingName,
      emailAddresses,
      additionalContactInfo,
    } = projectInfo;

    const payload = {
      clientPreferredId,
      clientId,
      projectDescription,
      projectName,
      userId,
      acknowledgedOn: ackTime,
      isAIEnabled: genAIEnabled,
      engagementCode: selectedAgreement?.engagementCode,
      overSeeingAttorney: {
        additionalContact: additionalContactInfo,
        emails: emailAddresses,
        names: overseeingName,
      },
    };

    try {
      const res = await createProject(payload);
      if (res.status == 201) {
        onSaveNContinue();
      }
    } catch (err) {
      showErrorToast(showToast, err, 'center');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Box className="review-and-acknoledgement-container">
        {/* Project Info */}
        <Box className="review-and-acknowledgement-section1">
          <Box className="review-and-acknoledgement-header">
            <Typography className="typo-text headline1_size">
              {t(STRING_PATH + 'confirmAllInfo')}
            </Typography>
            <AurButton
              type="continue"
              label={t(STRING_PATH + 'editDetails')}
              size="Medium"
              onClick={onEditDetails}
            />
          </Box>

          <Box className="review-and-acknowledgement-project-info">
            <Box display="flex" flexDirection="column" gap="24px" flex={1}>
              <Typography className="typo-text subhead2_size value-text">
                {projectInfo.projectName}
              </Typography>
              <KeyValueRow
                label={t(STRING_PATH + 'description')}
                value={projectInfo.projectDescription}
              />
              <Box display="flex" justifyContent="space-between">
                <KeyValueRow
                  label={t(STRING_PATH + 'clientPreferredID')}
                  value={projectInfo.clientId}
                />
                <KeyValueRow
                  label={t(STRING_PATH + 'aIInvestigate')}
                  value={t(
                    STRING_PATH + `${genAIEnabled ? 'enabled' : 'disabled'}`
                  )}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" gap="24px" flex={1}>
              <Typography className="typo-text subhead2_size value-text">
                {t(STRING_PATH + 'overseeingAttorney')}
              </Typography>
              <KeyValueRow
                label={t(STRING_PATH + 'names')}
                value={projectInfo.overseeingName}
              />
              <KeyValueRow
                label={t(STRING_PATH + 'emailAddresses')}
                value={projectInfo.emailAddresses}
              />
              <KeyValueRow
                label={t(STRING_PATH + 'additionalContactInfo')}
                value={projectInfo.additionalContactInfo}
              />
            </Box>
          </Box>
        </Box>

        {/* Billing & Acknowledgement */}
        {selectedAgreement && (
          <Box className="review-and-acknowledgement-section2">
            <Typography className="typo-text headline2_size value-text">
              {t(STRING_PATH + 'billingAgreement')}
            </Typography>
            <Box className="billing-agreement-info">
              <Box className="billing-agreemen-section1">
                <Typography className="typo-text subhead2_size value-text">
                  [{selectedAgreement.engagementName}] |{' '}
                  {selectedAgreement.engagementCode}
                </Typography>
                <Box display="flex" justifyContent="space-between">
                  <KeyValueRow
                    label={t(STRING_PATH + 'type')}
                    value={selectedAgreement.engagementType}
                  />
                  <KeyValueRow
                    label={t(STRING_PATH + 'eCIAccess')}
                    value={t(
                      STRING_PATH +
                        `${selectedAgreement.eciAccessEnabled ? 'enabled' : 'disabled'}`
                    )}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <KeyValueRow
                    label={t(STRING_PATH + 'reviewPlatform')}
                    value={selectedAgreement.reviewPlatform}
                  />
                  <KeyValueRow
                    label={t(STRING_PATH + 'geography')}
                    value={selectedAgreement.countryName}
                    geography={selectedAgreement.geography}
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <KeyValueRow
                    label={t(STRING_PATH + 'projectServiceType')}
                    value={selectedAgreement.serviceType}
                  />
                </Box>
              </Box>
              <Box className="billing-agreemen-section2">
                {IS_HOT_LEAD_FORM_VISIBLE && hotLeadBanner}
              </Box>
            </Box>

            {/* Acknowledgement */}
            <Box className="acknowledgment">
              <FormControlLabel
                control={
                  <AurCheckbox checked={confirmed} onChange={handleConfirm} />
                }
                label={
                  <Typography className="typo-text subhead3_size ack-checkbox-label">
                    {t(STRING_PATH + 'acknowledgementText')}
                  </Typography>
                }
                className="ack-checkbox"
              />
              <Box className="acknowledgement-text-field">
                <Typography
                  variant="body2"
                  className={`typo-text subhead3_size ack-heading3 ${confirmed ? 'ack-heading-checked' : ''}`}
                >
                  {t(STRING_PATH + 'acknowledgementDesc')}
                </Typography>
                <AurTextField
                  error={isError}
                  value={ackText}
                  disabled={!confirmed}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText={isError ? ackErrorMsg : undefined}
                  label={t(STRING_PATH + 'ackPlaceholder')}
                  variant="filled"
                  className="aur-textfield acknowledgment-input"
                  InputProps={{
                    endAdornment: isError ? (
                      <FailedIcon height={20} width={20} />
                    ) : null,
                  }}
                />
                {ackTimestamp && !isError && (
                  <FormHelperText className="ack-timestamp">
                    {ackTimestamp}
                  </FormHelperText>
                )}
              </Box>
            </Box>
          </Box>
        )}

        {/* Submit */}
        <Box className="submit-button-container">
          <AurButton
            type="continue"
            label={t(STRING_PATH + 'submitAndContinue')}
            size="Large"
            classNames="save-button"
            dataTestId="SubmitAndContinue"
            disabled={!canSubmit}
            onClick={onReviewSubmit}
            keepDisabledFocusable
          />
        </Box>
      </Box>
      <AurLoadingIndicator overlay={true} isOpen={isLoading} />
    </>
  );
};
export default ReviewAndAcknoledgement;
